import React from 'react';

const SLIDE_REFERENCE_WIDTH = 960;
const SLIDE_REFERENCE_HEIGHT = 540;
const ARROW_BBOX_MIN_PX = 6;

const cornerCoords = (corner, w, h) => {
  switch (corner) {
    case 'tr': return [w, 0];
    case 'bl': return [0, h];
    case 'br': return [w, h];
    case 'tl':
    default: return [0, 0];
  }
};

const oppositeCorner = (corner) => {
  switch (corner) {
    case 'tl': return 'br';
    case 'tr': return 'bl';
    case 'bl': return 'tr';
    case 'br':
    default: return 'tl';
  }
};

// Smooth curve through a series of points using quadratic Bezier through the
// midpoints of consecutive samples. This is the classic "smooth handwriting"
// technique — noisy drag input ends up as a clean, continuous curve.
export const buildSmoothPath = (points) => {
  if (!points || points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const mx = (points[i].x + points[i + 1].x) / 2;
    const my = (points[i].y + points[i + 1].y) / 2;
    d += ` Q ${points[i].x} ${points[i].y} ${mx} ${my}`;
  }
  const last = points[points.length - 1];
  d += ` L ${last.x} ${last.y}`;
  return d;
};

const ArrowElement = ({ element }) => {
  const widthPct = element?.size?.width ?? 0;
  const heightPct = element?.size?.height ?? 0;
  const Wpx = Math.max((widthPct / 100) * SLIDE_REFERENCE_WIDTH, ARROW_BBOX_MIN_PX);
  const Hpx = Math.max((heightPct / 100) * SLIDE_REFERENCE_HEIGHT, ARROW_BBOX_MIN_PX);

  const stroke = element?.content?.stroke || '#1e1e1e';
  const strokeWidth = Math.max(1, element?.content?.strokeWidth ?? 3);
  const headSize = Math.max(strokeWidth * 2.5, element?.content?.headSize ?? strokeWidth * 3);

  const mode = element?.content?.mode || 'straight';
  const isCurved = mode === 'curved'
    && Array.isArray(element?.content?.points)
    && element.content.points.length >= 2;

  // Compute the tip and the incoming tangent so we can both draw the arrowhead
  // and trim the line/path back so it doesn't poke through the head.
  let tipX, tipY, prevX, prevY;
  let pathD = null;

  if (isCurved) {
    const px = element.content.points.map(p => ({
      x: (p.x / 100) * Wpx,
      y: (p.y / 100) * Hpx,
    }));
    const last = px[px.length - 1];
    const prev = px[px.length - 2];
    tipX = last.x;
    tipY = last.y;
    prevX = prev.x;
    prevY = prev.y;

    // Trim the last segment so the path stops at the base of the arrowhead.
    const dx = last.x - prev.x;
    const dy = last.y - prev.y;
    const segLen = Math.hypot(dx, dy) || 1;
    const trim = Math.min(headSize * 0.6, segLen * 0.9);
    const ratio = (segLen - trim) / segLen;
    const trimmedLast = {
      x: prev.x + dx * ratio,
      y: prev.y + dy * ratio,
    };
    pathD = buildSmoothPath([...px.slice(0, -1), trimmedLast]);
  } else {
    const startCorner = element?.content?.startCorner || 'tl';
    const endCorner = oppositeCorner(startCorner);
    const [sx, sy] = cornerCoords(startCorner, Wpx, Hpx);
    const [ex, ey] = cornerCoords(endCorner, Wpx, Hpx);
    tipX = ex;
    tipY = ey;
    prevX = sx;
    prevY = sy;
  }

  const angle = Math.atan2(tipY - prevY, tipX - prevX);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const baseX = tipX - cos * headSize;
  const baseY = tipY - sin * headSize;
  const halfW = headSize * 0.5;
  const headLeft = `${baseX - sin * halfW},${baseY + cos * halfW}`;
  const headRight = `${baseX + sin * halfW},${baseY - cos * halfW}`;
  const headTip = `${tipX},${tipY}`;

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${Wpx} ${Hpx}`}
      preserveAspectRatio="none"
      style={{ overflow: 'visible', pointerEvents: 'none' }}
    >
      {isCurved ? (
        <path
          d={pathD}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      ) : (
        <line
          x1={prevX}
          y1={prevY}
          x2={tipX - cos * headSize * 0.6}
          y2={tipY - sin * headSize * 0.6}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      )}
      <polygon
        points={`${headTip} ${headLeft} ${headRight}`}
        fill={stroke}
      />
    </svg>
  );
};

export default ArrowElement;
